import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Breadcrumb,
  Button,
  Space,
  Table,
  Tag,
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  Avatar,
  message,
  Popconfirm,
  Divider,
  Badge,
  Alert,
  Checkbox,
  Upload,
  Image,
  Progress,
  Radio
} from 'antd';
import {
  HomeOutlined,
  TeamOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  UploadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import locale from 'antd/es/date-picker/locale/zh_CN';
import {
  dutyScheduleService,
  DutySchedule,
  DutyDay,
  DutyMember,
  LocationConfig,
  DutyGroup,
  DutyIncompleteRecord
} from '../../services/dutyScheduleService';
import { getMembers } from '../../services/api';
import { User } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { buildDemoDutySchedule, getDemoDutyGroups, getDemoMembers } from '../../demo/demoBrowseMocks';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { Link } from 'react-router-dom';
import './labActivitiesPage.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 获取图片完整URL的辅助函数
const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  // 如果已经是完整URL（http/https开头），直接返回
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // 确保路径以 / 开头
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  // 在生产环境，使用 window.location.origin 拼接路径
  // 在开发环境，如果前端运行在3000端口，后端在 REACT_APP_API_BASE（默认 5055），需要使用 API_BASE
  if (typeof window !== 'undefined' && window.location) {
    const isDev = window.location.port === '3000';
    if (isDev) {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:5055';
      return `${API_BASE}${normalizedPath}`;
    } else {
      // 生产环境：使用当前域名
      return `${window.location.origin}${normalizedPath}`;
    }
  }
  
  // 如果无法获取 window，返回原路径（不应该发生）
  return normalizedPath;
};

dayjs.locale('zh-cn');

const DutySchedulePage: React.FC = () => {
  const { user, role, isDemoBrowseOnly } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState<DutySchedule | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [showBatchGenerateModal, setShowBatchGenerateModal] = useState(false);
  const [showUploadRecordModal, setShowUploadRecordModal] = useState(false);
  const [showViewRecordModal, setShowViewRecordModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedMemberIndex, setSelectedMemberIndex] = useState<number>(-1);
  const [selectedMember, setSelectedMember] = useState<DutyMember | null>(null);
  const [batchGenerateForm] = Form.useForm();
  const [uploadRecordForm] = Form.useForm();
  const [uploadingImages, setUploadingImages] = useState<string[]>([]);
  const [areaImages, setAreaImages] = useState<Record<string, string[]>>({});
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [incompleteRecords, setIncompleteRecords] = useState<DutyIncompleteRecord[]>([]);
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<Dayjs>(dayjs()); // 当前查看的周
  const [dutyGroups, setDutyGroups] = useState<DutyGroup[]>([]);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupForm] = Form.useForm();
  const [showDeleteScopeModal, setShowDeleteScopeModal] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // 获取周次字符串
  const getWeekString = (date: Dayjs): string => {
    const d = date.toDate();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    const startOfYear = new Date(monday.getFullYear(), 0, 1);
    const pastDaysOfYear = (monday.getTime() - startOfYear.getTime()) / 86400000;
    const weekNumber = Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
    return `${monday.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
  };

  // 获取周的起止日期
  const getWeekRange = (date: Dayjs) => {
    const d = date.toDate();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = dayjs(d.setDate(diff));
    const sunday = monday.add(6, 'day');
    return { start: monday, end: sunday };
  };

  // 获取成员列表
  const fetchMembers = async () => {
    if (isDemoBrowseOnly) {
      setMembers(getDemoMembers());
      return;
    }
    try {
      const response = await getMembers();
      if (response.success) {
        setMembers(response.data || []);
      }
    } catch (error) {
      console.error('获取成员列表失败:', error);
    }
  };

  // 获取指定周的值日表
  const fetchScheduleByWeek = async (weekDate: Dayjs) => {
    try {
      setLoading(true);
      const weekString = getWeekString(weekDate);
      const weekRange = getWeekRange(weekDate);
      if (isDemoBrowseOnly) {
        setCurrentSchedule(
          buildDemoDutySchedule(
            weekString,
            weekRange.start.format('YYYY-MM-DD'),
            weekRange.end.format('YYYY-MM-DD')
          )
        );
        return;
      }
      try {
        const schedule = await dutyScheduleService.getByWeek(weekString);
        setCurrentSchedule(schedule);
      } catch (error: any) {
        // 如果该周不存在值日表，返回空结构
        setCurrentSchedule({
          week: weekString,
          weekStartDate: weekRange.start.format('YYYY-MM-DD'),
          weekEndDate: weekRange.end.format('YYYY-MM-DD'),
          schedule: [],
          isActive: false,
          autoRotate: false,
          notificationSettings: { remindBeforeDay: 1, remindOnDay: true },
          createdBy: ''
        });
      }
    } catch (error: any) {
      message.error(error.message || '获取值日表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取当前周值日表（用于初始化）
  const fetchCurrentSchedule = async () => {
    await fetchScheduleByWeek(dayjs());
  };

  const fetchIncompleteRecords = async () => {
    if (isDemoBrowseOnly) {
      setIncompleteRecords([]);
      return;
    }
    setIncompleteLoading(true);
    try {
      const data = await dutyScheduleService.getIncompleteRecords();
      setIncompleteRecords(data || []);
    } catch (error) {
      console.error('获取未完成值日记录失败:', error);
    } finally {
      setIncompleteLoading(false);
    }
  };

  // 初始化：获取成员列表和当前周值日表
  useEffect(() => {
    fetchMembers();
    fetchCurrentSchedule();
    fetchIncompleteRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoBrowseOnly]);

  const canManageIncomplete = () => {
    if (role === '超级管理员' || role === '管理员') return true;
    return !!(user as any)?.permissions?.duty;
  };

  const handleDeleteIncompleteRecord = async (record: DutyIncompleteRecord) => {
    try {
      await dutyScheduleService.deleteIncompleteRecord(record._id);
      message.success('已删除该未完成值日记录');
      await fetchIncompleteRecords();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  // 创建值日成员组
  const handleCreateGroup = async () => {
    try {
      const values = await groupForm.validateFields();
      const payload = {
        name: values.name as string,
        description: values.description as string | undefined,
        memberIds: (values.memberIds || []) as string[]
      };
      if (!payload.memberIds || payload.memberIds.length === 0) {
        message.error('请选择至少一位成员');
        return;
      }
      const newGroup = await dutyScheduleService.createDutyGroup(payload);
      setDutyGroups(prev => [newGroup, ...prev]);
      message.success('成员组已创建');
      setGroupModalVisible(false);
    } catch (error: any) {
      if (error?.errorFields) {
        // 表单校验错误，忽略
        return;
      }
      console.error('创建成员组失败:', error);
      message.error(error.message || '创建成员组失败');
    }
  };

  // 加载值日成员组
  useEffect(() => {
    const fetchGroups = async () => {
      if (isDemoBrowseOnly) {
        setDutyGroups(getDemoDutyGroups());
        return;
      }
      try {
        const groups = await dutyScheduleService.getDutyGroups();
        setDutyGroups(groups || []);
      } catch (error) {
        console.error('获取值日成员组失败:', error);
      }
    };
    fetchGroups();
  }, [isDemoBrowseOnly]);

  // 当选择的周改变时，重新加载值日表
  // 使用 ref 来跟踪是否是首次渲染
  const isFirstRender = useRef(true);
  useEffect(() => {
    // 跳过首次渲染，因为初始化时已经加载了当前周
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchScheduleByWeek(selectedWeek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);


  // 批量自动生成值日表（多周和不同地点）
  const handleBatchAutoGenerate = async (values: any) => {
    try {
      setLoading(true);
      
      // 计算开始周和结束周的周一日期
      const startWeekRange = getWeekRange(dayjs(values.startWeek));
      const endWeekRange = getWeekRange(dayjs(values.endWeek));
      
      // 验证周数（最多26周，约半年）
      const weeksDiff = endWeekRange.start.diff(startWeekRange.start, 'week') + 1;
      if (weeksDiff > 26) {
        message.error('最多只能一次性生成26周（约半年）的值日表');
        setLoading(false);
        return;
      }
      
      // 构建地点配置，并强制规范化成员 ID / 组 ID
      const locationConfigs: LocationConfig[] = values.locationConfigs.map((config: any) => {
        // 规范化成员ID：确保始终是字符串数组
        let memberIds: string[] = [];
        if (Array.isArray(config.memberIds)) {
          memberIds = config.memberIds as string[];
        } else if (typeof config.memberIds === 'string' && config.memberIds.trim()) {
          // 某些情况下，如果用户在选择器中输入了文字但未正确选择选项，表单值可能是姓名而不是ID
          const name = config.memberIds.trim();
          const matched = members.find(m =>
            m._id === name ||
            m.name === name ||
            `${m.name} (${m.studentId})` === name ||
            name.includes(m.name)
          );
          if (matched && typeof matched._id === 'string') {
            memberIds = [matched._id];
          }
        }

        // 规范化组ID
        const groupIds: string[] = Array.isArray(config.groupIds) ? config.groupIds as string[] : [];

        return {
          location: config.location,
          memberIds,
          frequency: config.frequency ? parseInt(config.frequency, 10) : 1, // 默认每天
          groupIds,
          mode: (config.mode as 'member' | 'group') || (groupIds && groupIds.length > 0 ? 'group' : 'member'),
          requiredAreas: config.requiredAreas || [],
          scheduleMode: (config.scheduleMode as 'frequency' | 'weeklyFixed') || 'frequency',
          weeklyDays: Array.isArray(config.weeklyDays) ? config.weeklyDays : []
        };
      });

      // 前端额外校验：每个地点根据排班方式必须选择成员或成员组
      for (let i = 0; i < locationConfigs.length; i++) {
        const cfg = locationConfigs[i];
        const label = cfg.location || `地点 ${i + 1}`;
        if (cfg.mode === 'member') {
          if (!cfg.memberIds || cfg.memberIds.length === 0) {
            message.error(`请为「${label}」选择至少一位值日成员`);
            setLoading(false);
            return;
          }
        } else {
          // mode === 'group'
          if (!cfg.groupIds || cfg.groupIds.length === 0) {
            message.error(`请为「${label}」选择至少一个成员组`);
            setLoading(false);
            return;
          }
        }
      }
      
      const result = await dutyScheduleService.batchAutoGenerate({
        startWeek: startWeekRange.start.format('YYYY-MM-DD'),
        endWeek: endWeekRange.end.format('YYYY-MM-DD'),
        locationConfigs,
        excludeDates: Array.isArray(values.excludeDates) ? values.excludeDates : [],
        excludeHolidays: values.excludeHolidays || false,
        excludeHolidaysAndWeekends: values.excludeHolidaysAndWeekends || false,
        excludeWeekends: values.excludeWeekends || false
      });

      message.success(result.message || `成功生成 ${result.data.length} 周的值日表`);
      setShowBatchGenerateModal(false);
      batchGenerateForm.resetFields();
      // 刷新当前周的值日表
      await fetchScheduleByWeek(selectedWeek);
    } catch (error: any) {
      message.error(error.message || '批量生成值日表失败');
    } finally {
      setLoading(false);
    }
  };

  // 值日签到
  const handleCheckIn = async (scheduleId: string, date: string, memberIndex: number) => {
    try {
      await dutyScheduleService.checkIn(scheduleId, date, memberIndex);
      message.success('签到成功');
      await fetchScheduleByWeek(selectedWeek);
    } catch (error: any) {
      message.error(error.message || '签到失败');
    }
  };

  // 值日签退
  const handleCheckOut = async (scheduleId: string, date: string, memberIndex: number) => {
    try {
      await dutyScheduleService.checkOut(scheduleId, date, memberIndex);
      message.success('签退成功');
      await fetchScheduleByWeek(selectedWeek);
    } catch (error: any) {
      message.error(error.message || '签退失败');
    }
  };

  // 上传值日记录
  const handleUploadRecord = async (values: any) => {
    try {
      if (!currentSchedule?._id) {
        message.error('当前值日表不存在');
        return;
      }
      
      setIsUploading(true);
      setUploadProgress(0);
      
      // 模拟上传进度（实际API调用是同步的，这里用setTimeout模拟进度）
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);
      
      try {
        // 计算必拍区域配置
        let requiredAreas: string[] = [];
        if (currentSchedule.dutyAreasConfig && selectedDate) {
          const day = currentSchedule.schedule.find(d => dayjs(d.date).isSame(dayjs(selectedDate), 'day'));
          const member = day && day.members[selectedMemberIndex];
          const memberLocation = member?.location || day?.location || undefined;
          if (memberLocation) {
            const cfg = currentSchedule.dutyAreasConfig.find(c => c.location === memberLocation);
            if (cfg && Array.isArray(cfg.areas)) {
              requiredAreas = cfg.areas;
            }
          }
        }

        // 计算 areaRecords
        const areaRecords = requiredAreas.map(areaName => ({
          areaName,
          images: areaImages[areaName] || []
        }));

        // 汇总所有图片（区域图片 + 额外图片）
        const allAreaImages = areaRecords.flatMap(ar => ar.images || []);
        const allImages = Array.from(new Set<string>([...allAreaImages, ...uploadingImages]));

        if (allImages.length === 0) {
          message.error('请至少上传一张值日图片');
          return;
        }

        await dutyScheduleService.uploadRecord(
          currentSchedule._id,
          selectedDate,
          selectedMemberIndex,
          allImages,
          values.description,
          areaRecords
        );
        
        clearInterval(progressInterval);
        setUploadProgress(100);
        
        // 等待一小段时间让用户看到100%进度
        await new Promise(resolve => setTimeout(resolve, 300));
        
        message.success('值日记录上传成功');
        setShowUploadRecordModal(false);
        uploadRecordForm.resetFields();
        setUploadingImages([]);
        setAreaImages({});
        setUploadProgress(0);
        await fetchScheduleByWeek(selectedWeek);
      } catch (error: any) {
        clearInterval(progressInterval);
        throw error;
      }
    } catch (error: any) {
      setUploadProgress(0);
      message.error(error.message || '上传值日记录失败');
    } finally {
      setIsUploading(false);
    }
  };

  // 查看值日记录
  const handleViewRecord = (member: DutyMember) => {
    setSelectedMember(member);
    setShowViewRecordModal(true);
  };

  // 上传图片处理
  const handleImageUpload = async (file: File): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      try {
        // 如果是HEIC格式，尝试转换为JPEG
        // 注意：浏览器可能不支持直接读取HEIC格式，如果转换失败会直接上传原文件
        let fileToUpload = file;
        const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                       file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
        
        if (isHeic) {
          try {
            // 尝试使用浏览器API转换HEIC为JPEG
            const image = document.createElement('img') as HTMLImageElement;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              throw new Error('无法创建canvas上下文');
            }
            
            const imageUrl = URL.createObjectURL(file);
            await new Promise<void>((imgResolve, imgReject) => {
              image.onload = () => {
                canvas.width = image.width;
                canvas.height = image.height;
                ctx.drawImage(image, 0, 0);
                URL.revokeObjectURL(imageUrl);
                imgResolve();
              };
              image.onerror = () => {
                URL.revokeObjectURL(imageUrl);
                imgReject(new Error('无法加载HEIC图片'));
              };
              image.src = imageUrl;
            });
            
            // 将canvas转换为blob
            canvas.toBlob((blob) => {
              if (blob) {
                fileToUpload = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
                uploadFile(fileToUpload);
              } else {
                // 如果转换失败，直接上传原文件（后端可能支持）
                console.warn('HEIC转JPEG失败，尝试直接上传原文件');
                uploadFile(file);
              }
            }, 'image/jpeg', 0.9);
            return;
          } catch (convertError) {
            // 转换失败，直接上传原文件（后端已支持所有image/*格式）
            console.warn('HEIC转换失败，尝试直接上传原文件:', convertError);
            fileToUpload = file;
          }
        }
        
        uploadFile(fileToUpload);
      } catch (error) {
        reject(error);
      }
      
      function uploadFile(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        
        const token = localStorage.getItem('token');
        fetch('/api/upload', {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: formData
        })
          .then(res => res.json())
          .then(data => {
            if (data.url) {
              resolve(data.url);
            } else {
              reject(new Error(data.error || '上传失败'));
            }
          })
          .catch(reject);
      }
    });
  };

  // 删除值日表：scope = 仅本周 | 本周及本周以后
  const handleDeleteScope = async (scope: 'week' | 'weekAndAfter') => {
    if (!currentSchedule?._id) return;
    setDeleteSubmitting(true);
    try {
      const result = await dutyScheduleService.delete(currentSchedule._id, scope);
      message.success(result?.message || '值日表已删除');
      setShowDeleteScopeModal(false);
      await fetchScheduleByWeek(selectedWeek);
    } catch (error: any) {
      message.error(error.message || '删除失败');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // 检查是否有删除权限
  const canDelete = () => {
    if (!currentSchedule) return false;
    if (role === '超级管理员' || role === '管理员') return true;
    // 检查是否是创建者
    const createdBy = typeof currentSchedule.createdBy === 'string' 
      ? currentSchedule.createdBy 
      : currentSchedule.createdBy._id;
    return user?.id === createdBy;
  };

  // 获取状态标签
  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      '待完成': { color: 'default', icon: <ClockCircleOutlined />, text: '待完成' },
      '进行中': { color: 'processing', icon: <ClockCircleOutlined />, text: '进行中' },
      '已完成': { color: 'success', icon: <CheckCircleOutlined />, text: '已完成' },
      '未完成': { color: 'error', icon: <CloseCircleOutlined />, text: '未完成' }
    };
    const config = statusMap[status] || statusMap['待完成'];
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // 获取用户信息
  const getUserInfo = (userId: string | { _id: string; name: string; avatar?: string }) => {
    if (typeof userId === 'string') {
      const member = members.find(m => m._id === userId);
      return member ? { name: member.name, avatar: member.avatar } : { name: '未知', avatar: undefined };
    }
    return { name: userId.name, avatar: userId.avatar };
  };

  /** 同一天同地点且同属至少一个值日组的成员合并为「按组值日」一张卡片 */
  const buildDutyMemberClusters = (
    day: DutyDay
  ): Array<
    | { kind: 'solo'; index: number }
    | { kind: 'group'; indices: number[]; title: string }
  > => {
    const list = day.members;
    const n = list.length;
    if (n === 0) return [];

    const locKey = (i: number) => list[i].location || day.location || '';

    const groupIdSetForIndex = (i: number): Set<string> => {
      const m = list[i];
      const uid = typeof m.userId === 'string' ? m.userId : m.userId._id;
      const ids = new Set<string>();
      for (const g of dutyGroups) {
        if (Array.isArray(g.memberIds) && g.memberIds.some(mid => mid._id === uid)) {
          ids.add(g._id);
        }
      }
      return ids;
    };

    const parent = Array.from({ length: n }, (_, i) => i);
    const find = (x: number): number => {
      if (parent[x] !== x) parent[x] = find(parent[x]);
      return parent[x];
    };
    const union = (a: number, b: number) => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[rb] = ra;
    };

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (locKey(i) !== locKey(j)) continue;
        const gi = groupIdSetForIndex(i);
        const gj = groupIdSetForIndex(j);
        if (gi.size === 0 || gj.size === 0) continue;
        const share = Array.from(gi).some((id) => gj.has(id));
        if (share) union(i, j);
      }
    }

    const rootToIndices = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
      const r = find(i);
      if (!rootToIndices.has(r)) rootToIndices.set(r, []);
      rootToIndices.get(r)!.push(i);
    }

    const clusters: Array<
      | { kind: 'solo'; index: number }
      | { kind: 'group'; indices: number[]; title: string }
    > = [];

    Array.from(rootToIndices.values()).forEach((indices) => {
      indices.sort((a, b) => a - b);
      const hasGroup = indices.some(i => groupIdSetForIndex(i).size > 0);
      if (indices.length === 1 && !hasGroup) {
        clusters.push({ kind: 'solo', index: indices[0] });
        return;
      }
      let common: Set<string> | null = null;
      for (const i of indices) {
        const s = groupIdSetForIndex(i);
        if (common === null) common = new Set(s);
        else common = new Set((Array.from(common) as string[]).filter((x) => s.has(x)));
      }
      const titleNames = dutyGroups.filter(g => common?.has(g._id)).map(g => g.name);
      const title = titleNames.length > 0 ? titleNames.join('、') : '组值日';
      clusters.push({ kind: 'group', indices, title });
    });

    clusters.sort((a, b) => {
      const ma = a.kind === 'solo' ? a.index : Math.min(...a.indices);
      const mb = b.kind === 'solo' ? b.index : Math.min(...b.indices);
      return ma - mb;
    });
    return clusters;
  };

  // 判断是否是当前用户的值日
  const isMyDuty = (member: DutyMember) => {
    if (!user) return false;
    const memberUserId = typeof member.userId === 'string' ? member.userId : member.userId._id;
    // 支持多种ID格式匹配：_id、id、username
    const userIds = [
      user._id,
      user.id,
      user.username
    ].filter(Boolean); // 过滤掉undefined/null
    
    return userIds.some(uid => String(uid) === String(memberUserId));
  };

  // 渲染值日表日历
  const renderCalendar = () => {
    if (!currentSchedule) return null;

    const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

    const renderMemberDutyBlock = (day: DutyDay, memberIndex: number) => {
      const member = day.members[memberIndex];
      const userInfo = getUserInfo(member.userId);
      const isMy = isMyDuty(member);
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
            <Avatar src={userInfo.avatar} icon={<UserOutlined />} size="small" style={{ marginRight: '8px' }} />
            <Text strong>{userInfo.name}</Text>
            <Tag color={member.dutyType === '值日' ? 'blue' : 'orange'} style={{ marginLeft: '8px' }}>
              {member.dutyType}
            </Tag>
            {member.location && (
              <Tag color="purple" style={{ marginLeft: '8px' }}>
                {member.location}
              </Tag>
            )}
          </div>

          <div style={{ marginBottom: '8px' }}>{getStatusTag(member.status)}</div>

          {member.checkInTime && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              签到: {dayjs(member.checkInTime).format('HH:mm')}
            </div>
          )}

          {member.checkOutTime && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              签退: {dayjs(member.checkOutTime).format('HH:mm')}
            </div>
          )}

          {member.notes && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
              {member.notes}
            </div>
          )}

          {member.dutyRecords && member.dutyRecords.length > 0 && (
            <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '8px' }}>
              <CheckCircleOutlined /> 已上传 {member.dutyRecords.length} 条记录
            </div>
          )}

          <Divider style={{ margin: '8px 0' }} />

          <Space size="small" wrap>
            {isMy && !member.checkInTime && (
              <Button
                size="small"
                type="primary"
                onClick={() => handleCheckIn(currentSchedule._id!, day.date, memberIndex)}
              >
                签到
              </Button>
            )}

            {isMy && member.checkInTime && !member.checkOutTime && (
              <Button size="small" onClick={() => handleCheckOut(currentSchedule._id!, day.date, memberIndex)}>
                签退
              </Button>
            )}

            {isMy &&
              (dayjs(day.date).isSame(dayjs(), 'day') || dayjs(day.date).isBefore(dayjs(), 'day')) &&
              member.status !== '已完成' && (
                <Button
                  size="small"
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setSelectedDate(day.date);
                    setSelectedMemberIndex(memberIndex);
                    uploadRecordForm.resetFields();
                    setUploadingImages([]);
                    setShowUploadRecordModal(true);
                  }}
                >
                  上传值日记录
                </Button>
              )}

            {member.dutyRecords && member.dutyRecords.length > 0 && (
              <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewRecord(member)}>
                查看记录 ({member.dutyRecords.length})
              </Button>
            )}
          </Space>
        </>
      );
    };
    
    return (
      <div style={{ marginTop: '24px' }}>
        <Row gutter={[16, 16]}>
          {currentSchedule.schedule.map((day, dayIndex) => {
            const date = dayjs(day.date);
            const isToday = date.isSame(dayjs(), 'day');
            
            return (
              <Col xs={24} sm={12} lg={8} xl={6} key={dayIndex}>
                <Card
                  size="small"
                  className={`lab-act-day-card${isToday ? ' lab-act-day-card--today' : ''}`}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                        <CalendarOutlined className="lab-act-schedule-title-icon" style={{ marginRight: 4 }} />
                        <span>{weekDays[day.dayOfWeek]} {date.format('MM-DD')}</span>
                        {/* 显示值日地点（不再显示频率，如“每天”“每周”） */}
                        {(() => {
                          // 获取日期级别的location，如果没有则从成员的location中获取
                          const dayLocation = day.location || (day.members.length > 0 ? day.members[0].location : null);
                          
                          if (!dayLocation) return null;
                          // 只显示地点，不显示频率文案
                          return (
                            <Tag color="purple" style={{ marginLeft: '4px', fontSize: '12px' }}>
                              {dayLocation}
                            </Tag>
                          );
                        })()}
                      </div>
                      {isToday && <Badge status="processing" text="今天" />}
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                >
                  {day.members.length === 0 ? (
                    <div className="lab-act-muted-hint" style={{ textAlign: 'center', padding: '20px' }}>
                      暂无值日安排
                    </div>
                  ) : (
                    <div>
                      {buildDutyMemberClusters(day).map((cl) => {
                        if (cl.kind === 'solo') {
                          const memberIndex = cl.index;
                          const member = day.members[memberIndex];
                          const isMy = isMyDuty(member);
                          return (
                            <div
                              key={`solo-${memberIndex}`}
                              className={`lab-act-duty-solo${isMy ? ' lab-act-duty-solo--mine' : ''}`}
                            >
                              {renderMemberDutyBlock(day, memberIndex)}
                            </div>
                          );
                        }
                        const anyMy = cl.indices.some((i) => isMyDuty(day.members[i]));
                        return (
                          <div
                            key={`group-${cl.indices.join('-')}`}
                            className={`lab-act-duty-group${anyMy ? ' lab-act-duty-group--mine' : ''}`}
                          >
                            <div className="lab-act-duty-group-h">
                              <TeamOutlined />
                              <Tag color={anyMy ? 'blue' : 'green'}>按组值日</Tag>
                              <Text strong>{cl.title}</Text>
                            </div>
                            <div style={{ padding: '8px 12px' }}>
                              {cl.indices.map((memberIndex, idx) => (
                                <React.Fragment key={memberIndex}>
                                  {idx > 0 && <Divider style={{ margin: '12px 0' }} />}
                                  <div
                                    style={{
                                      padding: '4px 0',
                                      borderRadius: '8px',
                                      backgroundColor: isMyDuty(day.members[memberIndex])
                                        ? 'rgba(0, 53, 95, 0.06)'
                                        : 'transparent'
                                    }}
                                  >
                                    {renderMemberDutyBlock(day, memberIndex)}
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };

  const isAdmin = role === '管理员' || role === '超级管理员';

  return (
    <div className="page-container full-height">
      <Breadcrumb
        className="lab-act-breadcrumb"
        items={[
          {
            title: (
              <Link to="/dashboard">
                <HomeOutlined />
                <span style={{ marginLeft: 6 }}>首页</span>
              </Link>
            )
          },
          { title: '实验室活动' },
          { title: '值日安排' }
        ]}
      />

      <div className="lab-act-page-header">
        <div className="lab-act-page-header-inner">
          <div className="lab-act-title-row">
            <div className="lab-act-title-icon" aria-hidden>
              <LimsNavIcon name="calendar_month" sizePx={26} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 className="page-title">值日安排</h1>
              <p className="page-subtitle">查看本周排班、上传值日记录；管理员可批量生成多周排班</p>
            </div>
          </div>
          {isAdmin && (
            <Button
              type="primary"
              className="lab-act-theme-primary-btn"
              icon={<PlusOutlined />}
              onClick={() => {
                const today = dayjs();
                batchGenerateForm.setFieldsValue({
                  startWeek: today,
                  endWeek: today.add(12, 'week'),
                  locationConfigs: [{ location: '', memberIds: [], frequency: 1 }],
                  excludeHolidays: false,
                  excludeHolidaysAndWeekends: false,
                  excludeWeekends: false,
                  excludeDates: []
                });
                setShowBatchGenerateModal(true);
              }}
            >
              批量生成
            </Button>
          )}
        </div>
      </div>

      <Card className="lab-act-toolbar-card" bordered={false}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16
          }}
        >
          <Space wrap align="center">
            <Text strong style={{ color: 'var(--lims-on)' }}>
              选择周次
            </Text>
            <DatePicker
              picker="week"
              value={selectedWeek}
              onChange={(date) => {
                if (date) {
                  setSelectedWeek(date);
                }
              }}
              locale={locale}
              format="YYYY 第 ww 周"
              style={{ width: 200 }}
              allowClear={false}
            />
            <Button
              onClick={() => {
                const today = dayjs();
                setSelectedWeek(today);
              }}
            >
              本周
            </Button>
            <Button
              onClick={() => {
                const prevWeek = selectedWeek.subtract(1, 'week');
                setSelectedWeek(prevWeek);
              }}
            >
              上一周
            </Button>
            <Button
              onClick={() => {
                const nextWeek = selectedWeek.add(1, 'week');
                setSelectedWeek(nextWeek);
              }}
            >
              下一周
            </Button>
          </Space>
          {currentSchedule?._id && canDelete() && (
            <Button danger icon={<DeleteOutlined />} disabled={loading} onClick={() => setShowDeleteScopeModal(true)}>
              删除值日表
            </Button>
          )}
        </div>
      </Card>

      {/* 删除值日表范围选择弹窗 */}
      <Modal
        title="删除值日表"
        open={showDeleteScopeModal}
        onCancel={() => !deleteSubmitting && setShowDeleteScopeModal(false)}
        footer={null}
        closable={!deleteSubmitting}
        maskClosable={!deleteSubmitting}
      >
        <div style={{ marginBottom: 16 }}>
          请选择删除范围。删除后无法恢复，请谨慎操作。
        </div>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button
            block
            danger
            loading={deleteSubmitting}
            onClick={() => handleDeleteScope('week')}
          >
            仅删除本周值日表
          </Button>
          <Button
            block
            danger
            loading={deleteSubmitting}
            onClick={() => handleDeleteScope('weekAndAfter')}
          >
            删除本周及本周以后的所有值日表
          </Button>
          <Button block onClick={() => !deleteSubmitting && setShowDeleteScopeModal(false)}>
            取消
          </Button>
        </Space>
      </Modal>

      <Card
        className="lab-act-panel-card"
        bordered={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <TeamOutlined className="lab-act-schedule-title-icon" />
              <span>
                {currentSchedule ? `${currentSchedule.week} 值日表` : '当前周值日表'}
              </span>
              {currentSchedule && (
                <Text type="secondary" style={{ marginLeft: 16, fontSize: 14 }}>
                  {dayjs(currentSchedule.weekStartDate).format('YYYY-MM-DD')} 至{' '}
                  {dayjs(currentSchedule.weekEndDate).format('YYYY-MM-DD')}
                </Text>
              )}
            </div>
            {currentSchedule?._id && canDelete() && (
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                disabled={loading}
                onClick={() => setShowDeleteScopeModal(true)}
              >
                删除
              </Button>
            )}
          </div>
        }
        loading={loading}
      >
        {currentSchedule && currentSchedule.schedule.length > 0 ? (
          renderCalendar()
        ) : (
          <div className="lab-act-empty-state">
            <TeamOutlined />
            <Title level={4} type="secondary" style={{ marginBottom: 0 }}>
              当前周暂无值日安排
            </Title>
            <Text type="secondary">管理员可使用「批量生成」创建多周排班</Text>
          </div>
        )}
      </Card>

      <Card
        className="lab-act-panel-card"
        title="未完成值日记录"
        bordered={false}
        extra={
          <Text type="secondary" className="lab-act-muted-hint">
            周期结束仍未上传记录时自动登记；有值日管理权限者可删除
          </Text>
        }
      >
        <Table
          rowKey="_id"
          loading={incompleteLoading}
          dataSource={incompleteRecords}
          pagination={incompleteRecords.length > 10 ? { pageSize: 10 } : false}
          size="small"
          locale={{ emptyText: '暂无未完成值日记录' }}
          columns={[
            {
              title: '成员',
              key: 'user',
              width: 140,
              render: (_: unknown, record: DutyIncompleteRecord) => {
                const populated = typeof record.userId === 'object' && record.userId
                  ? record.userId
                  : null;
                return populated?.name || record.userName || populated?.username || record.username || '-';
              }
            },
            {
              title: '地点',
              dataIndex: 'location',
              key: 'location',
              width: 120,
              render: (v: string) => v || '-'
            },
            {
              title: '说明',
              dataIndex: 'message',
              key: 'message',
              render: (v: string) => v || '-'
            },
            {
              title: '登记时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 170,
              render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-')
            },
            ...(canManageIncomplete()
              ? [{
                  title: '操作',
                  key: 'action',
                  width: 100,
                  render: (_: unknown, record: DutyIncompleteRecord) => (
                    <Popconfirm
                      title="确认删除该未完成值日记录？"
                      onConfirm={() => handleDeleteIncompleteRecord(record)}
                      okText="删除"
                      cancelText="取消"
                    >
                      <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                        删除
                      </Button>
                    </Popconfirm>
                  )
                }]
              : [])
          ]}
        />
      </Card>

      <Card className="lab-act-rules-card" title="值日规则说明" bordered={false}>
        <Typography.Paragraph>
          <Text strong>1. 值日完成判定</Text>
          <br />
          - 只有在上传了值日记录图片后，系统才会将值日状态标记为“已完成”。<br />
          - 对于组值日，当同一地点、同一日期的组内任意一名成员上传了值日记录时，该组当天的所有组内成员都会被视为“已完成”，且系统会把同一份上传记录同步到组内每一人的值日记录下（便于查看与统计一致）。
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Text strong>2. 当天 22:00 未完成提醒</Text>
          <br />
          - 在值日周期的最后一天晚上 22:00，系统会检查当天的值日是否已上传记录。<br />
          - 如果仍未完成，系统会发送值日未完成提醒（系统通知，并根据个人偏好可能发送邮件）。
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Text strong>3. 当天 23:59 未完成值日登记</Text>
          <br />
          - 在值日周期的最后一天晚上 23:59，系统会再次检查是否仍未完成值日。<br />
          - 如仍未完成，系统会在本页值日表下方的「未完成值日记录」中登记一条记录，方便全员查看（不再写入「最近活动」）。<br />
          - 拥有值日管理权限的管理员可删除该列表中的记录。
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Text strong>4. 批量生成排班时的成员 / 组规则</Text>
          <br />
          - 在批量生成值日表时，每个地点可以选择“按成员”或“按组”两种排班方式（二选一）。<br />
          - 按成员：按照配置的成员顺序和设置的值日频率进行轮换排班。<br />
          - 按组：按照配置的组顺序和设置的值日频率，以“组”为单位依次排班，轮到某一组时，该组内所有成员一起值日。<br />
          - 排班生成后顺序固定，不会因请假、出差或逾期未完成而动态调整。
        </Typography.Paragraph>
      </Card>

      {/* 批量自动生成值日表模态框（多周和不同地点） */}
      <Modal
        title="批量生成值日表（最多26周）"
        open={showBatchGenerateModal}
        onCancel={() => {
          setShowBatchGenerateModal(false);
          batchGenerateForm.resetFields();
        }}
        onOk={() => batchGenerateForm.submit()}
        confirmLoading={loading}
        width={800}
      >
        <Form form={batchGenerateForm} onFinish={handleBatchAutoGenerate} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startWeek"
                label="开始周"
                rules={[{ required: true, message: '请选择开始周' }]}
              >
                <DatePicker
                  picker="week"
                  locale={locale}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endWeek"
                label="结束周"
                rules={[{ required: true, message: '请选择结束周' }]}
              >
                <DatePicker
                  picker="week"
                  locale={locale}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label="值日地点配置"
            required
            tooltip="可以为不同地点配置不同的值日成员，每天每个地点都会安排值日人员"
          >
            <Form.List name="locationConfigs">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => {
                    // 避免将 key 透传到 Form.Item，按 React 要求单独作为 JSX 属性使用
                    const { key, ...restField } = field;
                    return (
                    <Card
                      key={key}
                      size="small"
                      title={`地点 ${index + 1}`}
                      extra={
                        fields.length > 1 && (
                          <Button
                            type="link"
                            danger
                            size="small"
                            onClick={() => remove(field.name)}
                          >
                            删除
                          </Button>
                        )
                      }
                      style={{ marginBottom: '16px' }}
                    >
                      <Form.Item
                        {...restField}
                        name={[field.name, 'location']}
                        label="地点名称"
                        rules={[{ required: true, message: '请输入地点名称' }]}
                      >
                        <Input placeholder="例如：实验室A、实验室B、办公室等" />
                      </Form.Item>
                      {/* 按频次排班时显示“值日频率”字段 */}
                      <Form.Item shouldUpdate={() => true} noStyle>
                        {() => {
                          const scheduleMode: 'frequency' | 'weeklyFixed' =
                            batchGenerateForm.getFieldValue(['locationConfigs', field.name, 'scheduleMode']) || 'frequency';
                          if (scheduleMode !== 'frequency') {
                            return null;
                          }
                          return (
                            <Form.Item
                              {...restField}
                              name={[field.name, 'frequency']}
                              label="值日频率"
                              tooltip="每x天值日一次，例如：1表示每天，2表示每2天一次，3表示每3天一次"
                              initialValue={1}
                              rules={[
                                { required: true, message: '请输入值日频率' },
                                { 
                                  validator: (_, value) => {
                                    const num = parseInt(value, 10);
                                    if (!value || isNaN(num) || num < 1 || num > 30) {
                                      return Promise.reject(new Error('频率必须是1-30之间的整数'));
                                    }
                                    return Promise.resolve();
                                  }
                                }
                              ]}
                              getValueFromEvent={(e) => {
                                const value = e.target.value;
                                return value === '' ? undefined : parseInt(value, 10);
                              }}
                              normalize={(value) => {
                                if (value === '' || value === undefined) return 1;
                                const num = parseInt(value, 10);
                                return isNaN(num) ? 1 : Math.max(1, Math.min(30, num));
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                <Input
                                  type="number"
                                  min={1}
                                  max={30}
                                  placeholder="每x天一次（1-30，默认1）"
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                                <span style={{ color: 'rgba(0,0,0,0.45)', flexShrink: 0 }}>天一次</span>
                              </div>
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[field.name, 'scheduleMode']}
                        label="排班模式"
                        initialValue="frequency"
                        tooltip="按频次：每x天一次；按每周固定：指定每周几轮流值日"
                      >
                        <Radio.Group>
                          <Radio.Button value="frequency">按频次排班</Radio.Button>
                          <Radio.Button value="weeklyFixed">按每周固定排班</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item
                        // 始终根据当前表单值重新渲染排班模式相关区域
                        shouldUpdate={() => true}
                        noStyle
                      >
                        {() => {
                          const scheduleMode: 'frequency' | 'weeklyFixed' =
                            batchGenerateForm.getFieldValue(['locationConfigs', field.name, 'scheduleMode']) || 'frequency';
                          const isWeeklyFixed = scheduleMode === 'weeklyFixed';
                          return (
                            <>
                              {isWeeklyFixed && (
                                <Form.Item
                                  {...restField}
                                  name={[field.name, 'weeklyDays']}
                                  label="每周固定排班的星期"
                                  tooltip="例如选择周一和周三，则每周一和周三按成员/组轮流值日"
                                  rules={[{ required: true, message: '请选择至少一个星期几' }]}
                                >
                                  <Checkbox.Group style={{ width: '100%' }}>
                                    <Row>
                                      <Col span={8}><Checkbox value={1}>周一</Checkbox></Col>
                                      <Col span={8}><Checkbox value={2}>周二</Checkbox></Col>
                                      <Col span={8}><Checkbox value={3}>周三</Checkbox></Col>
                                      <Col span={8}><Checkbox value={4}>周四</Checkbox></Col>
                                      <Col span={8}><Checkbox value={5}>周五</Checkbox></Col>
                                      <Col span={8}><Checkbox value={6}>周六</Checkbox></Col>
                                      <Col span={8}><Checkbox value={0}>周日</Checkbox></Col>
                                    </Row>
                                  </Checkbox.Group>
                                </Form.Item>
                              )}
                              {/* 当按每周固定排班时，频次输入仅用于兼容，设为只读提示 */}
                              <Form.Item
                                shouldUpdate={false}
                                style={{ marginBottom: isWeeklyFixed ? 0 : 24 }}
                              >
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {isWeeklyFixed
                                    ? '当前采用每周固定排班：仅在选择的星期几安排该地点的值日，并按成员/组每周轮换。'
                                    : '当前采用按频次排班：从开始周起每隔指定天数安排一次该地点的值日。'}
                                </Text>
                              </Form.Item>
                            </>
                          );
                        }}
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[field.name, 'mode']}
                        label="排班方式"
                        initialValue="member"
                      >
                        <Radio.Group>
                          <Radio.Button value="member">按成员</Radio.Button>
                          <Radio.Button value="group">按组</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item
                        label="值日成员"
                        // 这里不直接作为字段，只作为外层标签，具体字段由内部两个 Select 分别绑定 memberIds / groupIds
                      >
                        <>
                          <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                            {/** 根据排班方式控制单人/分组二选一 */}
                            {(() => {
                              const mode: 'member' | 'group' =
                                batchGenerateForm.getFieldValue(['locationConfigs', field.name, 'mode']) || 'member';
                              const isMemberMode = mode === 'member';
                              const isGroupMode = mode === 'group';
                              
                              return (
                                <>
                                  {/* 按成员选择：绑定到 memberIds 字段 */}
                                  <Form.Item
                                    {...restField}
                                    name={[field.name, 'memberIds']}
                                    style={{ flex: 1, marginBottom: 0 }}
                                  >
                                    <Select
                                      mode="multiple"
                                      placeholder="请选择该地点的值日成员"
                                      showSearch
                                      disabled={isGroupMode}
                                      filterOption={(input, option) => {
                                        const children = option?.children;
                                        const text = typeof children === 'string' 
                                          ? children 
                                          : String(children ?? '');
                                        return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                      }}
                                    >
                                      {members.map(member => (
                                        <Option key={member._id} value={member._id}>
                                          {member.name} ({member.studentId})
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                  {/* 按组选择：绑定到 groupIds 字段 */}
                                  <Form.Item
                                    {...restField}
                                    name={[field.name, 'groupIds']}
                                    style={{ width: 220, marginLeft: 8, marginBottom: 0 }}
                                  >
                                    <Select
                                      mode="multiple"
                                      placeholder="选择成员组"
                                      allowClear
                                      disabled={isMemberMode}
                                    >
                                      {dutyGroups.map(group => (
                                        <Option key={group._id} value={group._id}>
                                          {group.name}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                  <Button
                                    type="default"
                                    style={{ marginLeft: 8 }}
                                    onClick={() => {
                                      // 打开创建成员组弹窗，并把当前已选成员带过去
                                      const currentIds: string[] = (batchGenerateForm.getFieldValue(['locationConfigs', field.name, 'memberIds']) || []) as string[];
                                      groupForm.setFieldsValue({
                                        name: '',
                                        description: '',
                                        memberIds: currentIds || []
                                      });
                                      setGroupModalVisible(true);
                                    }}
                                  >
                                    添加组
                                  </Button>
                                </>
                              );
                            })()}
                          </Space.Compact>
                        </>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[field.name, 'requiredAreas']}
                        label="必拍区域（可选）"
                        tooltip="为该地点设置需要单独上传图片的区域，例如：地面、操作台、通道等"
                      >
                        <Select
                          mode="tags"
                          placeholder="输入区域名称后按回车添加，例如：地面、操作台等"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Card>
                    );
                  })}
                  <Button
                    type="dashed"
                    onClick={() => add({ location: '', memberIds: [], frequency: 1 })}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加地点
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          
          <Form.Item label="排除选项">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item name="excludeHolidays" valuePropName="checked" noStyle>
                <Checkbox>排除法定节假日（不排除周末）</Checkbox>
              </Form.Item>
              <Form.Item name="excludeHolidaysAndWeekends" valuePropName="checked" noStyle>
                <Checkbox>排除法定节假日（且排除周末）</Checkbox>
              </Form.Item>
              <Form.Item name="excludeWeekends" valuePropName="checked" noStyle>
                <Checkbox>排除周末</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="excludeDates" label="自定义排除日期（可选）">
            <Select
              mode="tags"
              placeholder="输入日期后按回车添加，格式：YYYY-MM-DD"
              tokenSeparators={[',']}
              style={{ width: '100%' }}
            />
          </Form.Item>
          
          <Alert
            message="提示"
            description="批量生成将一次性创建多周的值日表，最多支持26周（约半年）。每个地点可以根据设置的频率安排值日（如每2天一次、每3天一次等），系统会自动轮换分配值日人员。"
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        </Form>
      </Modal>

      {/* 创建值日成员组弹窗 */}
      <Modal
        title="创建值日成员组"
        open={groupModalVisible}
        onOk={handleCreateGroup}
        onCancel={() => setGroupModalVisible(false)}
        destroyOnHidden
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            name="name"
            label="组名"
            rules={[{ required: true, message: '请输入组名' }]}
          >
            <Input placeholder="例如：实验室值日组A" />
          </Form.Item>
          <Form.Item name="description" label="备注（可选）">
            <TextArea rows={2} placeholder="例如：主要负责实验室区域的值日" />
          </Form.Item>
          <Form.Item
            name="memberIds"
            label="组内成员"
            rules={[{ required: true, message: '请选择组内成员' }]}
          >
            <Select
              mode="multiple"
              placeholder="请选择组内成员"
              showSearch
              filterOption={(input, option) => {
                const children = option?.children;
                const text = typeof children === 'string'
                  ? children
                  : String(children ?? '');
                return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {members.map(member => (
                <Option key={member._id} value={member._id}>
                  {member.name} ({member.studentId})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 上传值日记录模态框 */}
      <Modal
        title="上传值日记录"
        open={showUploadRecordModal}
        onCancel={() => {
          if (!isUploading) {
            setShowUploadRecordModal(false);
            uploadRecordForm.resetFields();
            setUploadingImages([]);
            setUploadProgress(0);
          }
        }}
        onOk={() => {
          if (!isUploading) {
            uploadRecordForm.submit();
          }
        }}
        okText={isUploading ? '上传中...' : '确定'}
        okButtonProps={{ disabled: isUploading, loading: isUploading }}
        cancelButtonProps={{ disabled: isUploading }}
        width={700}
      >
        <Form form={uploadRecordForm} onFinish={handleUploadRecord} layout="vertical">
          {(() => {
            if (!currentSchedule || !selectedDate) return null;
            const day = currentSchedule.schedule.find(d => dayjs(d.date).isSame(dayjs(selectedDate), 'day'));
            if (!day) return null;
            const member = day.members[selectedMemberIndex];
            const memberLocation = member?.location || day.location || undefined;
            let requiredAreas: string[] = [];
            if (memberLocation && currentSchedule.dutyAreasConfig && currentSchedule.dutyAreasConfig.length > 0) {
              const cfg = currentSchedule.dutyAreasConfig.find(c => c.location === memberLocation);
              if (cfg && Array.isArray(cfg.areas)) {
                requiredAreas = cfg.areas;
              }
            }

            return (
              <>
                {requiredAreas.length > 0 && (
                  <Alert
                    message="必拍区域说明"
                    description={
                      <span>
                        该值日地点要求针对以下区域分别上传图片：{requiredAreas.join('、')}。每个区域至少需要上传一张图片，可以多张。
                      </span>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {requiredAreas.length > 0 && requiredAreas.map((areaName) => (
                  <Form.Item
                    key={areaName}
                    label={`区域：${areaName}`}
                    required
                  >
                    <Upload
                      listType="picture-card"
                      fileList={(areaImages[areaName] || []).map((url, index) => ({
                        uid: `${areaName}-${index}`,
                        name: `${areaName}-image-${index}.jpg`,
                        status: 'done',
                        url: getImageUrl(url)
                      }))}
                      beforeUpload={async (file) => {
                        try {
                          const url = await handleImageUpload(file);
                          setAreaImages(prev => {
                            const current = prev[areaName] || [];
                            return {
                              ...prev,
                              [areaName]: [...current, url]
                            };
                          });
                          return false;
                        } catch (error: any) {
                          message.error(error.message || '图片上传失败');
                          return false;
                        }
                      }}
                      onRemove={(file) => {
                        setAreaImages(prev => {
                          const current = prev[areaName] || [];
                          const newList = current.filter((_, index) => file.uid !== `${areaName}-${index}`);
                          return {
                            ...prev,
                            [areaName]: newList
                          };
                        });
                      }}
                      accept="image/*"
                      multiple
                      disabled={isUploading}
                    >
                      {(areaImages[areaName]?.length || 0) < 10 && !isUploading && (
                        <div><PlusOutlined /> 上传图片</div>
                      )}
                    </Upload>
                  </Form.Item>
                ))}

                <Form.Item
                  label={requiredAreas.length > 0 ? '其他补充图片（可选）' : '值日图片'}
                >
                  <Upload
                    listType="picture-card"
                    fileList={uploadingImages.map((url, index) => ({
                      uid: `extra-${index}`,
                      name: `image-${index}.jpg`,
                      status: 'done',
                      url: getImageUrl(url)
                    }))}
                    beforeUpload={async (file) => {
                      try {
                        const url = await handleImageUpload(file);
                        setUploadingImages(prev => [...prev, url]);
                        return false; // 阻止自动上传
                      } catch (error: any) {
                        message.error(error.message || '图片上传失败');
                        return false;
                      }
                    }}
                    onRemove={(file) => {
                      const newImages = uploadingImages.filter((_, index) => 
                        file.uid !== `extra-${index}`
                      );
                      setUploadingImages(newImages);
                    }}
                    accept="image/*"
                    multiple
                    disabled={isUploading}
                  >
                    {uploadingImages.length < 10 && !isUploading && <div><PlusOutlined /> 上传图片</div>}
                  </Upload>
                  <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                    最多可上传10张图片，支持JPG、PNG、WebP、HEIC等所有常见图片格式（HEIC格式会自动转换为JPEG）
                  </Text>
                </Form.Item>
              </>
            );
          })()}
          <Form.Item name="description" label="值日描述（可选）">
            <TextArea rows={4} placeholder="请输入值日情况描述，如：已完成清洁、设备检查正常等" disabled={isUploading} />
          </Form.Item>
          {isUploading && (
            <Form.Item label="上传进度">
              <Progress 
                percent={uploadProgress} 
                status={uploadProgress === 100 ? 'success' : 'active'}
                showInfo
              />
            </Form.Item>
          )}
          <Alert
            message="提示"
            description="上传值日记录后，值日状态将自动切换为'已完成'。所有用户都可以查看当天的值日记录。"
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        </Form>
      </Modal>

      {/* 查看值日记录模态框 */}
      <Modal
        title="值日记录"
        open={showViewRecordModal}
        onCancel={() => {
          setShowViewRecordModal(false);
          setSelectedMember(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setShowViewRecordModal(false);
            setSelectedMember(null);
          }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedMember && selectedMember.dutyRecords && selectedMember.dutyRecords.length > 0 ? (
          <div>
            {selectedMember.dutyRecords.map((record, recordIndex) => {
              const uploader = typeof record.uploadedBy === 'string' 
                ? members.find(m => m._id === record.uploadedBy)?.name || '未知'
                : record.uploadedBy.name;
              
              return (
                <Card
                  key={recordIndex}
                  size="small"
                  title={
                    <div>
                      <Text strong>记录 {recordIndex + 1}</Text>
                      <Text type="secondary" style={{ marginLeft: '16px', fontSize: '12px' }}>
                        上传时间：{dayjs(record.uploadedAt).format('YYYY-MM-DD HH:mm')}
                      </Text>
                      <Text type="secondary" style={{ marginLeft: '16px', fontSize: '12px' }}>
                        上传人：{uploader}
                      </Text>
                    </div>
                  }
                  style={{ marginBottom: '16px' }}
                >
                  {record.description && (
                    <div style={{ marginBottom: '16px' }}>
                      <Text strong>描述：</Text>
                      <Text>{record.description}</Text>
                    </div>
                  )}
                  {/* 按区域显示图片（如果有区域信息），否则按原方式显示 */}
                  {record.areaRecords && record.areaRecords.length > 0 ? (
                    <div>
                      {record.areaRecords.map((ar, idx) => (
                        <div key={idx} style={{ marginBottom: '12px' }}>
                          <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                            区域：{ar.areaName}
                          </Text>
                          {ar.images && ar.images.length > 0 && (
                            <Image.PreviewGroup>
                              <Row gutter={[8, 8]}>
                                {ar.images.map((imageUrl, imgIndex) => (
                                  <Col span={6} key={imgIndex}>
                                    <Image
                                      src={getImageUrl(imageUrl)}
                                      alt={`值日图片 ${imgIndex + 1}`}
                                      style={{ width: '100%', borderRadius: '4px' }}
                                    />
                                  </Col>
                                ))}
                              </Row>
                            </Image.PreviewGroup>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : record.images && record.images.length > 0 && (
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: '8px' }}>值日图片：</Text>
                      <Image.PreviewGroup>
                        <Row gutter={[8, 8]}>
                          {record.images.map((imageUrl, imgIndex) => (
                            <Col span={6} key={imgIndex}>
                              <Image
                                src={getImageUrl(imageUrl)}
                                alt={`值日图片 ${imgIndex + 1}`}
                                style={{ width: '100%', borderRadius: '4px' }}
                              />
                            </Col>
                          ))}
                        </Row>
                      </Image.PreviewGroup>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">暂无值日记录</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DutySchedulePage;
